import { Component, computed, effect, input, linkedSignal, signal } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatIcon } from '@angular/material/icon';
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { Combobox, ComboboxPopup, ComboboxWidget } from '@angular/aria/combobox';
import { Listbox, Option } from '@angular/aria/listbox';
import { TJunoSelectOption } from '@dss-libs/shared/type/common-types';
import { FormValueClassAbstract } from '../../../abstract/form-value-class-abstract';
import { JunoError } from '../juno-error/juno-error';
import { containDropdownWheel } from '../../../utils/dropdown-wheel.util';

/**
 * A styled, accessible autocomplete field built on {@link FormValueClassAbstract},
 * `@angular/aria` combobox/listbox primitives, and integrated with Angular Signal Forms.
 *
 * The component places `[ngCombobox]` on a native `<input>` so the user can type to filter
 * options. The Combobox directive owns all keyboard handling (Escape, ArrowDown/Up, Enter)
 * and the full ARIA combobox contract. A `Listbox` inside a `CdkConnectedOverlay` provides
 * the option list; selection flows back via `(valueChange)` → `onListboxValueChange`.
 *
 * Two separate signals carry different concerns:
 * - `filterText` — what is shown in the `<input>` and what narrows the options list.
 * - `value` (from the abstract) — the selected option's value, surfaced to Signal Forms.
 *
 * ---
 *
 * ### Inputs (own)
 *
 * | Input     | Type              | Default | Description                      |
 * |-----------|-------------------|---------|----------------------------------|
 * | `options` | `ISelectOption[]` | `[]`    | Full list of selectable options. |
 *
 * ### Inputs (inherited from {@link FormValueClassAbstract})
 *
 * `value`, `label`, `labelClass`, `placeholder`, `name`, `appliedClasses` (string —
 * applied to the trigger wrapper for width control), `required`, `errors`, `pending`,
 * `touched`, `dirty`, `disabled`.
 *
 * ### Outputs (inherited)
 *
 * `touch` – emitted when the popup closes so Signal Forms marks the field as touched.
 *
 * ---
 *
 * @usageNotes
 *
 * #### With Signal Forms (`[formField]`)
 * ```html
 * <juno-libs-ui-autocomplete
 *   [formField]="form.assignee"
 *   label="LABEL"
 *   [options]="userOptions()"
 *   appliedClasses="w-full"
 * />
 * ```
 *
 * #### Standalone two-way binding (`[(value)]`)
 * ```html
 * <juno-libs-ui-autocomplete
 *   [(value)]="selectedUserId"
 *   [options]="userOptions()"
 * />
 * ```
 *
 * @see {@link FormValueClassAbstract} for the full list of inherited inputs/outputs.
 * @see {@link JunoSelect} for the non-searchable dropdown counterpart.
 */
@Component({
  selector: 'juno-libs-ui-autocomplete',
  imports: [
    TranslocoDirective,
    CdkConnectedOverlay,
    Combobox,
    ComboboxPopup,
    ComboboxWidget,
    Listbox,
    Option,
    MatIcon,
    JunoError,
  ],
  templateUrl: './juno-autocomplete.html',
  styleUrl: './juno-autocomplete.css',
})
export class JunoAutocomplete extends FormValueClassAbstract<string | null, string> {
  protected readonly onDropdownWheel = containDropdownWheel;

  readonly options = input<TJunoSelectOption<string>[]>([]);
  readonly noResultsKey = input<string>('NO-RESULTS');

  protected readonly popupExpanded = signal(false);
  protected filterText = signal('');

  protected readonly selectedLabel = computed(
    () => this.options().find((option) => option.value === this.value())?.label ?? ''
  );

  protected readonly filteredOptions = computed(() => {
    const query = this.filterText().toLowerCase().trim();
    return query ? this.options().filter((option) => option.label.toLowerCase().includes(query)) : this.options();
  });

  protected readonly listboxValue = linkedSignal<string[]>(() => {
    const value = this.value();
    return value ? [value] : [];
  });

  readonly syncSelectedLabel = effect(() => {
    if (!this.popupExpanded()) {
      this.filterText.set(this.selectedLabel());
    }
  });

  protected openDropdown(): void {
    if (this.disabled() || this.pending() || this.popupExpanded()) return;
    this.filterText.set('');
    this.popupExpanded.set(true);
  }

  protected onExpandedChange(isExpanded: boolean): void {
    if (isExpanded && (this.disabled() || this.pending())) return;
    this.popupExpanded.set(isExpanded);
    if (isExpanded) {
      this.filterText.set('');
    } else {
      this.filterText.set(this.selectedLabel());
      this.touch.emit();
    }
  }

  protected onListboxValueChange(values: string[]): void {
    const [selected] = values;
    if (!selected) return;
    this.value.set(selected);
    this.onExpandedChange(false);
  }
}
